import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { Upload, CompareArrows } from "@mui/icons-material";

const DEVICES_LOCAL_STORAGE_KEY = "devices_data";

const ComparisonPage = () => {
  const theme = useTheme();
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectionFor, setSelectionFor] = useState("dataset1");
  const [dataset1, setDataset1] = useState(null);
  const [dataset2, setDataset2] = useState(null);
  
  const fileInputRef = useRef(null);

  // Load devices from localStorage
  useEffect(() => {
    const storedDevices = JSON.parse(localStorage.getItem(DEVICES_LOCAL_STORAGE_KEY)) || [];
    const devicesWithHistory = storedDevices.map(device => ({
      ...device,
      history: device.history || {}
    }));
    setDevices(devicesWithHistory);
  }, []);

  // Load datasets when device is selected
  useEffect(() => {
    if (!selectedDevice || !selectedDevice.history) return;

    const historyDatasets = Object.entries(selectedDevice.history).map(([date, data]) => ({
      id: date,
      timestamp: date,
      data: data
    }));

    setDatasets(historyDatasets);
  }, [selectedDevice]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadProgress(0);
    setShowUploadModal(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const data = new Uint8Array(arrayBuffer);

        const processedData = [];
        for (let i = 0; i < data.length; i += 24) {
          processedData.push({
            time: new Date().getTime() + i * 1000,
            ambientTemp: data[i],
            objectTemp: data[i+1],
            weight: data[i+2],
            current: data[i+3],
            rpm: data[i+4],
            dutyCycle: data[i+5],
          });
        }

        setFileData({
          id: `uploaded_${Date.now()}`,
          name: file.name,
          timestamp: new Date().toISOString(),
          data: processedData
        });

        setUploadProgress(100);
      } catch (error) {
        console.error("Error processing file:", error);
        setUploadProgress(0);
      }
    };

    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleUseUploadedData = () => {
    if (!fileData) return;
    
    if (selectionFor === "dataset1") {
      setDataset1(fileData);
    } else {
      setDataset2(fileData);
    }
    
    setShowUploadModal(false);
    setFileData(null);
    setUploadProgress(0);
  };

  const calculateStatistics = (data) => {
    if (!data || data.length === 0) return null;

     const invalidThresholds = {
        ambientTemp: -100,
        objectTemp: -100,
        current: -100,
        rpm: -100,
        weight: -100,
        dutyCycle: -10
    };
    
    const stats = {
      ambientTemp: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      objectTemp: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      current: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      rpm: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      weight: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      dutyCycle: { min: Infinity, max: -Infinity, avg: 0, validCount: 0 },
      duration: (data[data.length - 1].time - data[0].time) / 1000,
      dataPoints: data.length
    };

    // Calculate sums for averages
    let ambientTempSum = 0;
    let objectTempSum = 0;
    let currentSum = 0;
    let rpmSum = 0;
    let weightSum = 0;
    let dutyCycleSum = 0;

    data.forEach(point => {
        
        const processValue = (value, statKey) => {
          if (value > invalidThresholds[statKey]) {
            if (value > stats[statKey].max) stats[statKey].max = value;
            if (value < stats[statKey].min) stats[statKey].min = value;
            stats[statKey].validCount++;
            return value;
          }
          return 0;
        };

         // Ambient Temp
        ambientTempSum += processValue(point.ambientTemp, 'ambientTemp');

        // Object Temp
        objectTempSum += processValue(point.objectTemp, 'objectTemp');

        // Current
        currentSum += processValue(point.current, 'current');

        // RPM
        rpmSum += processValue(point.rpm, 'rpm');

        // Weight
        weightSum += processValue(point.weight, 'weight');

        // Duty Cycle
        dutyCycleSum += processValue(point.dutyCycle, 'dutyCycle');
    });

    const calculateAvg = (sum, statKey) => {
        return stats[statKey].validCount > 0 
          ? sum / stats[statKey].validCount 
          : null;
    };

    // Calculate averages
    stats.ambientTemp.avg = calculateAvg(ambientTempSum, 'ambientTemp');
    stats.objectTemp.avg = calculateAvg(objectTempSum, 'objectTemp');
    stats.current.avg = calculateAvg(currentSum, 'current');
    stats.rpm.avg = calculateAvg(rpmSum, 'rpm');
    stats.weight.avg = calculateAvg(weightSum, 'weight');
    stats.dutyCycle.avg = calculateAvg(dutyCycleSum, 'dutyCycle');

    const handleInvalidStats = (statKey) => {
        if (stats[statKey].validCount === 0) {
          stats[statKey].min = null;
          stats[statKey].max = null;
        }
    };

    handleInvalidStats('ambientTemp');
    handleInvalidStats('objectTemp');
    handleInvalidStats('current');
    handleInvalidStats('rpm');
    handleInvalidStats('weight');
    handleInvalidStats('dutyCycle');

    return stats;
  };

  const compareDatasets = () => {
    if (!dataset1 || !dataset2) return;
    
    const stats1 = calculateStatistics(dataset1.data);
    const stats2 = calculateStatistics(dataset2.data);
    
    if (!stats1 || !stats2) return;
    
    setComparisonData({
      dataset1: stats1,
      dataset2: stats2,
      differences: {
        ambientTemp: calculateParameterDifferences(stats1.ambientTemp, stats2.ambientTemp),
        objectTemp: calculateParameterDifferences(stats1.objectTemp, stats2.objectTemp),
        current: calculateParameterDifferences(stats1.current, stats2.current),
        rpm: calculateParameterDifferences(stats1.rpm, stats2.rpm),
        weight: calculateParameterDifferences(stats1.weight, stats2.weight),
        dutyCycle: calculateParameterDifferences(stats1.dutyCycle, stats2.dutyCycle),
        durationDiff: stats2.duration - stats1.duration,
        dataPointsDiff: stats2.dataPoints - stats1.dataPoints
      }
    });
  };

    const calculateParameterDifferences = (stat1, stat2) => {
      const hasValid1 = stat1.validCount > 0;
      const hasValid2 = stat2.validCount > 0;

      if (!hasValid1 && !hasValid2) {
        return { minDiff: null, maxDiff: null, avgDiff: null };
      }

      return {
        minDiff: hasValid1 && hasValid2 ? stat2.min - stat1.min : null,
        maxDiff: hasValid1 && hasValid2 ? stat2.max - stat1.max : null,
        avgDiff: hasValid1 && hasValid2 ? stat2.avg - stat1.avg : null
      };
    };

  const formatValue = (value, unit = "") => {
    if (value === null || value === undefined) return "N/A";
    if (value === Infinity) return "N/A";
    if (value === -Infinity) return "N/A";
    if (Math.abs(value) > 1000) return value.toFixed(0) + unit;
    if (Math.abs(value) > 10) return value.toFixed(1) + unit;
    return value.toFixed(2) + unit;
  };

  const getDiffColor = (value) => {
    if (value > 0) return theme.palette.success.main;
    if (value < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      p: 4,
      backgroundColor: theme.palette.primary.main
    }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: theme.palette.text.color }}>
          Dataset Comparison
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.color, mt: 1 }}>
          Compare two datasets to analyze differences
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Dataset 1 Selection */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.tertiary.main }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: theme.palette.text.color, mb: 2 }}>
                Dataset 1
              </Typography>
              
              {dataset1 ? (
                <>
                  <Typography variant="body1" sx={{ color: theme.palette.text.color }}>
                    {dataset1.name || "Stored Dataset"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {new Date(dataset1.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {dataset1.data.length} data points
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => {
                      setSelectionFor("dataset1");
                      setShowDeviceModal(true);
                    }}
                    sx={{
                        mt:2,
                        backgroundColor: "#006BCE",
                        color: '#fff',
                        textTransform: 'none'
                    }}
                  >
                    Change Dataset
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outlined"
                    color="info"
                    startIcon={<Upload />}
                    onClick={() => {
                      setSelectionFor("dataset1");
                      fileInputRef.current.click();
                    }}
                  >
                    Upload File
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Dataset 2 Selection */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.tertiary.main }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: theme.palette.text.color, mb: 2 }}>
                Dataset 2
              </Typography>
              
              {dataset2 ? (
                <>
                  <Typography variant="body1" sx={{ color: theme.palette.text.color }}>
                    {dataset2.name || "Stored Dataset"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {new Date(dataset2.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {dataset2.data.length} data points
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setSelectionFor("dataset2");
                      setShowDeviceModal(true);
                    }}
                    sx={{
                        mt:2,
                        backgroundColor: "#006BCE",
                        color: '#fff',
                        textTransform: 'none'
                    }}
                  >
                    Change Dataset
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outlined"
                    startIcon={<Upload />}
                    onClick={() => {
                      setSelectionFor("dataset2");
                      fileInputRef.current.click();
                    }}
                    color="info"
                  >
                    Upload File
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          color="info"
          size="large"
          startIcon={<CompareArrows />}
          onClick={compareDatasets}
          disabled={!dataset1 || !dataset2}
          
        >
          Compare Datasets
        </Button>
      </Box>

      {comparisonData && (
        <Card sx={{ backgroundColor: theme.palette.tertiary.main, mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: theme.palette.text.color, mb: 2 }}>
              Comparison Summary
            </Typography>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Parameter</TableCell>
                    <TableCell align="right">Dataset 1</TableCell>
                    <TableCell align="right">Dataset 2</TableCell>
                    <TableCell align="right">Difference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Ambient Temperature */}
                  <TableRow>
                    <TableCell>Ambient Temp (°C)</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.ambientTemp.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.ambientTemp.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.ambientTemp.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.ambientTemp.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.ambientTemp.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.ambientTemp.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.ambientTemp.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.ambientTemp.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.ambientTemp.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.ambientTemp.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Object Temperature */}
                  <TableRow>
                    <TableCell>Object Temp (°C)</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.objectTemp.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.objectTemp.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.objectTemp.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.objectTemp.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.objectTemp.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.objectTemp.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.objectTemp.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.objectTemp.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.objectTemp.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.objectTemp.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Current */}
                  <TableRow>
                    <TableCell>Current (A)</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.current.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.current.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.current.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.current.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.current.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.current.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.current.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.current.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.current.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.current.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* RPM */}
                  <TableRow>
                    <TableCell>RPM</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.rpm.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.rpm.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.rpm.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.rpm.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.rpm.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.rpm.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.rpm.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.rpm.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.rpm.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.rpm.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Weight */}
                  <TableRow>
                    <TableCell>Weight (g)</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.weight.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.weight.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.weight.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.weight.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.weight.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.weight.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.weight.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.weight.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.weight.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.weight.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Duty Cycle */}
                  <TableRow>
                    <TableCell>Duty Cycle (%)</TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset1.dutyCycle.min)}<br />
                      Max: {formatValue(comparisonData.dataset1.dutyCycle.max)}<br />
                      Avg: {formatValue(comparisonData.dataset1.dutyCycle.avg)}
                    </TableCell>
                    <TableCell align="right">
                      Min: {formatValue(comparisonData.dataset2.dutyCycle.min)}<br />
                      Max: {formatValue(comparisonData.dataset2.dutyCycle.max)}<br />
                      Avg: {formatValue(comparisonData.dataset2.dutyCycle.avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.dutyCycle.avgDiff) }}>
                      Min: {formatValue(comparisonData.differences.dutyCycle.minDiff)}<br />
                      Max: {formatValue(comparisonData.differences.dutyCycle.maxDiff)}<br />
                      Avg: {formatValue(comparisonData.differences.dutyCycle.avgDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Duration */}
                  <TableRow>
                    <TableCell>Duration (s)</TableCell>
                    <TableCell align="right">{formatValue(comparisonData.dataset1.duration)}</TableCell>
                    <TableCell align="right">{formatValue(comparisonData.dataset2.duration)}</TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.durationDiff) }}>
                      {formatValue(comparisonData.differences.durationDiff)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Data Points */}
                  <TableRow>
                    <TableCell>Data Points</TableCell>
                    <TableCell align="right">{comparisonData.dataset1.dataPoints}</TableCell>
                    <TableCell align="right">{comparisonData.dataset2.dataPoints}</TableCell>
                    <TableCell align="right" sx={{ color: getDiffColor(comparisonData.differences.dataPointsDiff) }}>
                      {comparisonData.differences.dataPointsDiff}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".bin"
        style={{ display: 'none' }}
      />

      {/* Device Selection Modal */}
      <Modal
        open={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '70%', md: '50%' },
          bgcolor: theme.palette.tertiary.main,
          boxShadow: 24,
          p: 4,
          borderRadius: 1
        }}>
          <Typography variant="h6" sx={{ 
            color: theme.palette.text.color,
            mb: 3
          }}>
            Select Device and Dataset
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ color: theme.palette.text.color }}>Device</InputLabel>
            <Select
              value={selectedDevice?.id || ""}
              onChange={(e) => setSelectedDevice(devices.find(d => d.id === e.target.value))}
              label="Device"
              sx={{
                color: theme.palette.text.color,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.border.color
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.secondary.main
                }
              }}
            >
              {devices.map(device => (
                <MenuItem 
                  key={device.id} 
                  value={device.id}
                  sx={{
                    color: theme.palette.text.color,
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.light
                    }
                  }}
                >
                  {device.deviceName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedDevice && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: theme.palette.text.color }}>Dataset</InputLabel>
              <Select
                value={""}
                onChange={(e) => {
                  const selected = datasets.find(d => d.id === e.target.value);
                  if (selectionFor === "dataset1") {
                    setDataset1(selected);
                  } else {
                    setDataset2(selected);
                  }
                  setShowDeviceModal(false);
                }}
                label="Dataset"
                sx={{
                  color: theme.palette.text.color,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.border.color
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main
                  }
                }}
              >
                {datasets.map(dataset => (
                  <MenuItem 
                    key={dataset.id} 
                    value={dataset.id}
                    sx={{
                      color: theme.palette.text.color,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.light
                      }
                    }}
                  >
                    {dataset.timestamp}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              onClick={() => setShowDeviceModal(false)}
              sx={{
                color: theme.palette.text.color,
                borderColor: theme.palette.border.color,
                '&:hover': {
                  borderColor: theme.palette.secondary.main
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* File Upload Modal */}
      <Modal
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadProgress(0);
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '70%', md: '50%' },
          bgcolor: theme.palette.tertiary.main,
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ 
            color: theme.palette.text.color,
            mb: 3
          }}>
            Upload Binary File
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {uploadProgress > 0 && uploadProgress < 100 ? (
              <>
                <CircularProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{ color: theme.palette.secondary.main }}
                />
                <Typography sx={{ color: theme.palette.text.color, mt: 2 }}>
                  Processing: {uploadProgress}%
                </Typography>
              </>
            ) : uploadProgress === 100 ? (
              <>
                <Typography sx={{ color: theme.palette.text.color, mb: 3 }}>
                  File processed successfully!
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleUseUploadedData}
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.hover.main
                    }
                  }}
                >
                  Use This Dataset
                </Button>
              </>
            ) : (
              <Typography sx={{ color: theme.palette.text.color }}>
                Please wait while we process your file...
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ComparisonPage;